import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SecurityContext,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AppService } from '@app/app.service';
import { enumBondRequestState, enumOcrStatus } from '@app/data-layer/enums/common';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@app/data-layer/enums/documents';
import { enumEntrySummarySubmissionType, enumTransportationMode } from '@app/data-layer/enums/entry-summaries';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { CompanyModel } from '@app/data-layer/models/companies';
import { isEntryLevelEsDocument, remapEntryLevelDocToFirstShipment } from '@helpers/documents.helper';
import { DocumentCreateModel, DocumentModel, DocumentPreviewModel } from '@app/data-layer/models/documents';
import { EntrySummaryModel, InvoiceModel } from '@app/data-layer/models/entry-summaries';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import {
  ALLOWED_FILE_EXTENSIONS,
  DEFAULT_LONGER_POLLING_TIMEOUT,
  DEFAULT_MAX_POLLING_RETRY,
  DEFAULT_POLLING_TIMEOUT,
} from '@constants/constants';
import { environment } from '@env/environment';
import { IDefaultSelect } from '@interfaces/default-select';
import { CompaniesService } from '@services/companies.service';
import { DocumentsService } from '@services/documents.service';
import { EntrySummaryStateService, EsOcrQuickFixService } from '@shared/es-mutual/services';
import { from, interval, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, switchMap, take, takeUntil, takeWhile, tap, toArray } from 'rxjs/operators';
import { EsPoaSidebarComponent } from '../es-poa-sidebar/es-poa-sidebar.component';
import { EsPoaSidebarComponentOld } from '../es-poa-sidebar-old/es-poa-sidebar.component';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ShipmentModel } from '@app/data-layer/models/common';
import { enumMimeType } from '@enum/enumMimeType';
import { enumFileExtension } from '@enum/enumFileExtension';
import { UtilsHelper } from '@helpers/utils-helper';
import { DocumentsRepository } from '@app/data-layer/repositories';
import { OverflowListenerDirective } from '@shared/directives/directives/overflow-listener.directive';

interface AttachedDocumentsModel {
  [key: number]: DocumentModel[];
}

@Component({
  selector: 'app-es-docs-strip',
  templateUrl: './es-docs-strip.component.html',
  styleUrls: ['./es-docs-strip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsDocsStripComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('pendingNonOcrFileInput') pendingNonOcrFileInput: ElementRef<HTMLInputElement>;
  @ViewChild(OverflowListenerDirective) overflowListener: OverflowListenerDirective;
  @ViewChildren('sectionElement') sectionElements!: QueryList<ElementRef>;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public shipments: ShipmentModel[];
  @Input() public topOffset: number = 65;

  public company: CompanyModel;
  public isLoading: boolean = false;
  public isForceAddDocument: boolean = false;
  public isAdditionalPseudoDoc: boolean = false;
  public isInvoiceReplace: boolean = false;
  public isOpenUploadModal: boolean = false;
  public isOpenDocsPreviewModal: boolean = false;
  public isEntryLoadingFirstTime: boolean = true;
  public docsPreviewUrls: Map<number, string> = new Map();
  public previewModalDocs: DocumentPreviewModel[] = [];
  public currentPreviewModalDocIndex: number = 0;
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public isLoadingDocUploadFlags: Map<string, boolean> = new Map();
  public isLoadingAdditionalDocUploadFlags: Map<string, boolean> = new Map();
  public defaultBlDocuments: enumDocumentType[] = [
    enumDocumentType.BILL_OF_LADING,
    enumDocumentType.ARRIVAL_NOTICE,
    enumDocumentType.PACKING_LIST,
  ];
  public readonly userNonDeletableDocTypes: enumDocumentType[] = [enumDocumentType.ACH_APPROVAL];

  public additionalDocumentSelectItems: IDefaultSelect[] = [];
  public additionalDocumentOptions: enumDocumentType[] = [
    enumDocumentType.INVOICE,
    enumDocumentType.ARRIVAL_NOTICE,
    enumDocumentType.PACKING_LIST,
    enumDocumentType.TRADE_CERTIFICATE,
    enumDocumentType.UNKNOWN,
  ];

  public scannableAdditionalDocs: enumDocumentType[] = this.additionalDocumentOptions;

  public entryLevelDocuments: DocumentModel[] = [];
  public skipOcrScanControl: UntypedFormControl;
  public currentDocument: DocumentModel;
  public currentShipmentId: number;
  public currentDocumentNumber: number;
  public isDocStripOverflowing: boolean = false;
  public isDocStripScrolledToStart: boolean = true;
  public isDocStripScrolledToEnd: boolean = false;
  public fileMimeIgnoreList: enumMimeType[] = [enumMimeType.XLS];
  public fileExtensionIgnoreList: enumFileExtension[] = [enumFileExtension.XLS];
  public documentChangeCounter: number = 0;
  public pendingDroppedFile: File | null = null;
  public isShowInvalidFileToast: boolean = false;
  public invalidFileErrorMessage: string = '';
  public dragOverCardKey: string | null = null;
  public pendingAdditionalDocuments: Map<number, DocumentModel[]> = new Map();

  private ocrStatus: enumOcrStatus;
  private entryDocuments: AttachedDocumentsModel = {};
  private rawDocuments: DocumentModel[] = [];
  private defaultScrollDistance = 200;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  private pendingNonOcrDoc: DocumentModel | null = null;
  private pendingNonOcrShipmentId: number | null = null;
  private overflowCheckPending: boolean = false;
  private pendingDocIdCounter: number = -100;

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private documentsRepository: DocumentsRepository,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private entrySummaryStateService: EntrySummaryStateService,
  ) {}

  ngOnInit(): void {
    this.skipOcrScanControl = new UntypedFormControl(false);

    this.additionalDocumentSelectItems = this.additionalDocumentOptions.map((type, i) => {
      const documentTypeName = enumDocumentType[type];
      return {
        id: i,
        value: type,
        label: `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`,
      };
    });
    this.company = this.companiesService.getCompany();
    this.isLoading = true;

    this.entrySummaryStateService.entrySummary$.pipe(takeUntil(this.destroy$)).subscribe((es) => {
      this.entrySummary = es;
      if (!!this.entrySummary && !!this.entrySummary?.id && this.isEntryLoadingFirstTime) {
        this.getEntryDocuments(true)
          .pipe(
            switchMap(() => this.getEntryDocumentsPreviewUrls(false)),
            finalize(() => {
              this.isLoading = false;
              this.cdr.detectChanges();
            }),
            takeUntil(this.destroy$),
          )
          .subscribe(
            (docsPreviewUrls) => {
              this.setEntryDocumentsPreviewUrls(docsPreviewUrls);
              this.isLoading = false;
              this.isEntryLoadingFirstTime = false;
            },
            () => (this.isLoading = false),
          );
      } else if (this.rawDocuments.length > 0) {
        this.entryDocuments = this.filteredDocuments(this.rawDocuments);
        this.overflowCheckPending = true;
      }
      this.cdr.detectChanges();
    });

    this.entrySummaryStateService.entryBlDocumentsUploaded$
      .pipe(
        mergeMap((docIds) => from(docIds)),
        mergeMap((docId) => this.pollEntryDocumentsPreviewUrl(docId)),
        finalize(() => {
          this.resetScrollingContainerParams();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((_) => {
        this.cdr.detectChanges();
      });

    this.entrySummaryStateService.currentEsDocOCRState$.pipe(takeUntil(this.destroy$)).subscribe((ocrStatus: enumOcrStatus) => {
      this.ocrStatus = ocrStatus;
      this.cdr.detectChanges();
    });

    this.entrySummaryStateService.uploadDocsStripDocument$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const { document, shipmentId, isForceAdd, isInvoiceReplace } = data;
      this.onDocumentUploadClick(document, shipmentId, isForceAdd, true, isInvoiceReplace);
    });

    this.entrySummaryStateService.shipmentRemoved$.pipe(takeUntil(this.destroy$)).subscribe((shipmentId) => {
      const poaDocTypes = [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];
      const shipmentDocuments = this.entryDocuments[shipmentId]?.filter((doc) => !!doc.createdDate && !poaDocTypes.includes(doc.type));
      shipmentDocuments.forEach((doc) => {
        this.deleteDocument(doc, shipmentId);
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shipments) {
      const previousShipmentsValue = changes.shipments.previousValue;
      const currentShipmentsValue = changes.shipments.currentValue;

      if (previousShipmentsValue?.length !== currentShipmentsValue?.length && !changes.shipments.firstChange) {
        this.resetScrollingContainerParams();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngAfterViewChecked(): void {
    if (this.overflowCheckPending && this.overflowListener) {
      this.overflowCheckPending = false;
      setTimeout(() => {
        if (this.overflowListener) {
          this.overflowListener.recalculate();
          this.cdr.markForCheck();
        }
      });
    }
  }

  public onDocumentUploadClick(
    document: DocumentModel,
    shipmentId: number,
    isForceAdd?: boolean,
    isAdditionalPseudoDoc?: boolean,
    isInvoiceReplace?: boolean,
  ) {
    if (this.isLoading || this.isDocumentUploadLoading(shipmentId, document)) {
      return;
    }

    const documentsOfType = (this.entryDocuments[shipmentId] || []).filter((doc) => doc.type === document.type);
    const indexOfCurrentDocumentOfType = documentsOfType.findIndex((doc) => doc.id === document.id);
    const documentNumber =
      (isAdditionalPseudoDoc || indexOfCurrentDocumentOfType === -1) && !isInvoiceReplace
        ? documentsOfType.length + 1
        : indexOfCurrentDocumentOfType + 1;

    this.documentChangeCounter++;
    this.currentDocument = document;
    this.currentShipmentId = shipmentId;
    this.currentDocumentNumber = documentNumber;
    this.isForceAddDocument = isForceAdd;
    this.isAdditionalPseudoDoc = isAdditionalPseudoDoc;
    this.isInvoiceReplace = isInvoiceReplace;
    if (this.isDocumentUploaded(document)) {
      return;
    }

    if (this.isNonOcrDocType(document)) {
      if (this.pendingNonOcrDoc !== null) {
        return;
      }
      this.pendingNonOcrDoc = document;
      this.pendingNonOcrShipmentId = shipmentId;
      this.pendingNonOcrFileInput?.nativeElement?.click();
      this.cdr.markForCheck();
      return;
    }

    this.setIsOpenUploadModal(true);
  }

  public onPreviewDocument(document: DocumentModel, shipmentId: number) {
    this.currentPreviewModalDocIndex = this.entryPreviewModalDocs.findIndex((pmd) => pmd.id === document.id);
    this.currentShipmentId = shipmentId;
    this.setIsOpenDocsPreviewModal(true);
  }

  public onPreviewDocumentDelete(data: { docId: number; shipmentId: number }) {
    const { docId, shipmentId: entityId } = data;

    let doc: DocumentModel;
    const currentShipment = this.entrySummary?.shipments?.find((sh) => sh.id === this.currentShipmentId);
    const invoices = currentShipment?.invoices;
    const invoice = invoices?.find((i) => i.id === entityId);

    if (entityId === this.entrySummary?.id) {
      doc = [...Object.values(this.entryDocuments)[0]].find((doc: DocumentModel) => {
        return doc.rootEntityId === this.entrySummary?.id && doc.id === docId;
      });
    } else if (!!invoice) {
      doc = [...Object.values(this.entryDocuments)[0]].find((doc: DocumentModel) => {
        return doc.relatedEntityId === entityId && doc.id === docId;
      });
    } else {
      doc = this.entryDocuments[entityId].find((doc) => doc.id === docId);
    }

    if (!doc) {
      return;
    }

    this.deleteDocument(doc, entityId);
  }

  public onAdditionalDocClick() {
    this.documentChangeCounter++;
  }

  public onMultipleDocTypeSelect(type: enumDocumentType, shipmentId: number) {
    const isInvoice = type === enumDocumentType.INVOICE;
    const serverDocsOfType = (this.entryDocuments[shipmentId] || []).filter((doc) => doc.type === type).length;
    const pendingDocsOfType = this.getPendingAdditionalDocs(shipmentId).filter((doc) => doc.type === type).length;
    const docNumber = serverDocsOfType + pendingDocsOfType + 1;

    const document: DocumentModel = {
      id: this.pendingDocIdCounter--,
      rootEntityId: this.entrySummary.id,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      relatedEntityId: shipmentId,
      relatedEntityType: isInvoice ? enumDocumentRelatedEntityType.INVOICE : enumDocumentRelatedEntityType.SHIPMENT,
      url: null,
      createdDate: null,
      fileName: null,
      type,
      state: null,
    };

    if (!this.pendingAdditionalDocuments.has(shipmentId)) {
      this.pendingAdditionalDocuments.set(shipmentId, []);
    }
    this.pendingAdditionalDocuments.get(shipmentId).push(document);
    this.isDocStripScrolledToEnd = false;
    this.overflowCheckPending = true;
    this.cdr.markForCheck();
  }

  public onPowerOfAttorneyClick(document: DocumentModel, shipmentId: number) {
    if (this.isLoading || this.isDocumentUploadLoading(shipmentId, document)) {
      return;
    }

    this.documentChangeCounter++;
    if (this.isDocumentUploaded(document)) {
      return;
    }

    const headerTitle = 'DOCUMENT_TYPE.POWER_OF_ATTORNEY';

    if (this.hasPoaDocusignFlag) {
      // Use new EsPoaSidebarComponent with DocuSign functionality
      const componentSidebarRef: ComponentRef<EsPoaSidebarComponent> = this.appService.openSidebarAsWindow(EsPoaSidebarComponent, {
        sidebarHeaderText: '',
        isMd: true,
      });

      componentSidebarRef.instance.company = this.company;
      componentSidebarRef.instance.entrySummary = this.entrySummary;
      componentSidebarRef.instance.headerTitle = headerTitle;
      componentSidebarRef.instance.shipmentId = shipmentId;
      componentSidebarRef.instance.docType = this.poaDocType;

      componentSidebarRef.instance.close.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.closeOpenedSidebar();
      });

      componentSidebarRef.instance.saveSelectedPoa.pipe(takeUntil(this.destroy$)).subscribe((response) => {
        const { document: selectedDoc, docType } = response;

        if (!this.entrySummary?.id) {
          this.closeOpenedSidebar();
          return;
        }

        // Capture existing POA doc ids before copy
        const existingDocs = [...Object.values(this.entryDocuments || {})].reduce((acc, curr) => acc.concat(curr), [] as DocumentModel[]);
        const existingPoaIds = existingDocs
          .filter(
            (d) =>
              d.type === docType &&
              d.rootEntityId === this.entrySummary.id &&
              d.rootEntityType === enumDocumentRootEntityType.ENTRY_SUMMARY,
          )
          .map((d) => d.id);

        this.isLoading = true;

        this.documentsService
          .copyDocToEntry(this.entrySummary.id, selectedDoc.id, selectedDoc.fileName, docType)
          .pipe(
            // Refresh entry documents so entryDocuments map includes the new POA
            switchMap(() => this.getEntryDocuments(false)),
            // Find the newly added POA doc and poll its preview until previewDocumentId is ready
            switchMap(() => {
              const allDocs = [...Object.values(this.entryDocuments || {})].reduce((acc, curr) => acc.concat(curr), [] as DocumentModel[]);
              const newPoaDoc =
                allDocs.find(
                  (d) =>
                    d.type === docType &&
                    d.rootEntityId === this.entrySummary.id &&
                    d.rootEntityType === enumDocumentRootEntityType.ENTRY_SUMMARY &&
                    !existingPoaIds.includes(d.id),
                ) || null;

              return newPoaDoc ? this.pollEntryDocumentsPreviewUrl(newPoaDoc.id) : of(null);
            }),
            finalize(() => {
              this.isLoading = false;
              this.resetScrollingContainerParams();
              this.cdr.detectChanges();
            }),
            takeUntil(this.destroy$),
          )
          .subscribe(() => {
            this.closeOpenedSidebar();
          });
      });
    } else {
      // Use old EsPoaSidebarComponentOld without DocuSign functionality
      const componentSidebarRef: ComponentRef<EsPoaSidebarComponentOld> = this.appService.openSidebarAsWindow(EsPoaSidebarComponentOld, {
        sidebarHeaderText: '',
        isMd: true,
      });

      componentSidebarRef.instance.company = this.company;
      componentSidebarRef.instance.entrySummary = this.entrySummary;
      componentSidebarRef.instance.headerTitle = headerTitle;
      componentSidebarRef.instance.shipmentId = shipmentId;
      componentSidebarRef.instance.docType = this.poaDocType;

      componentSidebarRef.instance.close.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.closeOpenedSidebar();
      });

      componentSidebarRef.instance.save.pipe(takeUntil(this.destroy$)).subscribe((response) => {
        this.isLoading = true;
        const { file, copyPoaToCompany } = response;
        this.uploadDocument(file, document, shipmentId, copyPoaToCompany);
        this.closeOpenedSidebar();
        this.cdr.detectChanges();
      });
    }
  }

  public closeOpenedSidebar() {
    this.appService.closeSidebarAsWindow();
  }

  public blDocuments(shipmentId: number) {
    const poaDocTypes = [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];

    const filteredDocuments = this.entryDocuments[shipmentId]?.filter((doc) => {
      const isEntryLevelDoc = this.entryLevelDocuments.some((eld) => eld.id === doc.id);

      return !poaDocTypes.includes(doc.type) && !isEntryLevelDoc;
    });
    return !!this.entryDocuments[shipmentId] ? filteredDocuments : [];
  }

  public customDocument(id: number, type: enumDocumentType, shipmentId: number) {
    if (!this.entryDocuments) {
      return;
    }

    const doc = this.entryDocuments[shipmentId]?.find((ed) => ed.type === type);
    if (!!doc) {
      return doc;
    }

    return {
      id,
      rootEntityId: this.entrySummary.id,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      relatedEntityId: shipmentId,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      url: null,
      createdDate: null,
      fileName: null,
      type,
      state: null,
    };
  }

  public document(doc: DocumentModel, shipmentId: number): DocumentModel {
    if (!this.entryDocuments) {
      return;
    }

    return this.entryDocuments[shipmentId]?.find((document: DocumentModel) => {
      const poaDocTypes = [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];
      let invoice: InvoiceModel;
      let comparisonId: number = shipmentId;
      if (doc.type === enumDocumentType.INVOICE) {
        const currentShipment = this.entrySummary?.shipments?.find((sh) => sh.id === shipmentId);
        const invoices = currentShipment?.invoices;
        invoice = invoices?.find((i) => i.id === doc.relatedEntityId);
        comparisonId = !!invoice ? invoice.id : shipmentId;
      } else if (poaDocTypes.includes(doc.type)) {
        comparisonId = this.entrySummary?.id;
      }
      return document.id === doc.id && doc.relatedEntityId === comparisonId;
    });
  }

  public documentPreviewUrl(doc: DocumentModel): string {
    if (!this.entryDocuments || !this.docsPreviewUrls) {
      return;
    }

    return this.docsPreviewUrls.get(doc.id);
  }

  public documentTagType(type: enumDocumentType): string {
    const requiredDocTypes = [enumDocumentType.INVOICE, enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];

    if (this.isBillOfLadingRequired) {
      requiredDocTypes.push(enumDocumentType.BILL_OF_LADING);
    }

    if (requiredDocTypes.includes(type)) {
      return 'alert';
    }

    if (type === enumDocumentType.ARRIVAL_NOTICE) {
      return 'warning';
    }

    return '';
  }

  public documentTagText(type: enumDocumentType): string {
    const requiredDocTypes = [enumDocumentType.INVOICE, enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];

    if (this.isBillOfLadingRequired) {
      requiredDocTypes.push(enumDocumentType.BILL_OF_LADING);
    }

    if (requiredDocTypes.includes(type)) {
      return 'LABELS.REQUIRED';
    }

    if (type === enumDocumentType.ARRIVAL_NOTICE) {
      return 'LABELS.REQUIRED_LATER';
    }

    return 'LABELS.OPTIONAL';
  }

  public documentTagTooltip(type: enumDocumentType): string {
    return type === enumDocumentType.ARRIVAL_NOTICE ? 'ENTRY_SUMMARY.BILL_OF_LADING_DOCS.ARRIVAL_NOTICE.REQUIRED_LATER_TOOLTIP' : '';
  }

  public documentLabel(type: enumDocumentType): string {
    const documentTypeName = enumDocumentType[type];
    return `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`;
  }

  public documentName(doc: DocumentModel, shipmentId?: number, docNumber?: number, showBlNumber?: boolean): string {
    const currentShipmentOfTypeDocs = (this.entryDocuments[shipmentId] || []).filter((cdotd) => {
      const isEntryLevelDoc = this.entryLevelDocuments.some((eld) => eld.id === cdotd.id);
      return cdotd.type === doc.type && !isEntryLevelDoc;
    });

    const indexOfCurrentShipmentOfTypeDoc = currentShipmentOfTypeDocs.findIndex((cdotd) => cdotd.id === doc.id);
    const documentTypeName = enumDocumentType[doc.type];

    if (doc.type === enumDocumentType.BILL_OF_LADING) {
      return (
        this.translateService.instant(`ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`) +
        (showBlNumber ? ` #${docNumber}` : '')
      );
    }

    // For server docs found in entryDocuments: use the index
    if (indexOfCurrentShipmentOfTypeDoc > 0) {
      return (
        this.translateService.instant(`ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`) +
        ` #${indexOfCurrentShipmentOfTypeDoc + 1}`
      );
    }

    // Pending docs are not found in entryDocuments (index === -1), so we use the passed docNumber for numbering
    const isPendingDocWithNumber: boolean = indexOfCurrentShipmentOfTypeDoc === -1 && docNumber && docNumber > 1;
    if (isPendingDocWithNumber) {
      return this.translateService.instant(`ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`) + ` #${docNumber}`;
    }

    return `ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`;
  }

  public documentUploadModalHeaderDocumentName(doc: DocumentModel, docNumber?: number) {
    const documentTypeName = enumDocumentType[doc.type];
    return this.translateService.instant(`ENTRY_SUMMARY.BILL_OF_LADING_DOCS.${documentTypeName}.TITLE`) + ` #${docNumber}`;
  }

  public isDocumentUploadLoading(shipmentId: number, doc?: DocumentModel): boolean {
    return this.isLoadingDocUploadFlags.get(doc.type + '-' + shipmentId + '-' + doc.id);
  }

  public isDocumentUploaded(doc: DocumentModel): boolean {
    return doc?.state === enumDocumentState.UPLOADED;
  }

  public uploadDocument(
    file: File,
    doc: DocumentModel,
    shipmentId: number,
    copyPoaToCompany: boolean = false,
    isForceAdd?: boolean,
    isAdditionalPseudoDoc?: boolean,
    isInvoiceReplace: boolean = false,
  ): void {
    if (this.entrySummary?.id) {
      this.uploadDocumentToServer(file, doc, shipmentId, copyPoaToCompany, isForceAdd, isAdditionalPseudoDoc, isInvoiceReplace);
      return;
    }
  }

  public uploadAdditionalDocument(event: { file: File; type: enumDocumentType }, shipmentId: number): void {
    const file = event.file;
    const type = event.type;

    if (this.entrySummary?.id) {
      this.uploadAdditionalDocumentToServer(file, type, shipmentId);
      return;
    }
  }

  public deleteDocument(doc: DocumentModel, shipmentId: number): void {
    if (this.entrySummary?.id) {
      this.deleteDocumentFromServer(doc, shipmentId);
      return;
    }
  }

  public updateDocumentState(state: enumDocumentState, doc: DocumentModel, shipmentId: number): void {
    if (!this.entryDocuments) {
      return;
    }

    this.entryDocuments[shipmentId] = this.entryDocuments[shipmentId]?.map((document: DocumentModel) =>
      document.id === doc?.id
        ? {
            ...document,
            state,
          }
        : document,
    );

    this.setDocumentIsLoading(`${doc?.type}-${shipmentId}-${doc?.id}`, false);
    this.isLoading = false;
  }

  public onBrowse(file: File) {
    if (!this.currentDocument || !this.currentShipmentId) {
      this.setIsOpenUploadModal(false);
      this.resetDocumentUploadFields();
      return;
    }

    const copyPoaToCompany = false;

    this.pendingDroppedFile = null;
    this.setIsOpenUploadModal(false);
    this.uploadDocument(
      file,
      this.currentDocument,
      this.currentShipmentId,
      copyPoaToCompany,
      this.isForceAddDocument,
      this.isAdditionalPseudoDoc,
      this.isInvoiceReplace,
    );
    setTimeout(() => {
      this.resetDocumentUploadFields();
    }, 500);
  }

  public getDropZoneKey(doc: DocumentModel, shipmentId: number): string {
    return `${doc.id}-${shipmentId}`;
  }

  public validateDroppedFile(file: File): { valid: boolean; errorMessage?: string } {
    const allowedExtensions = ALLOWED_FILE_EXTENSIONS.filter((ext) => !this.fileExtensionIgnoreList.includes(ext as enumFileExtension));
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!allowedExtensions.includes(extension as enumFileExtension)) {
      return {
        valid: false,
        errorMessage: this.translateService.instant('FORM_ERROR.FILE_EXTENSION_NOT_SUPPORTED', { fileName: file.name }),
      };
    }
    const fileSizeMB = file.size / 1024 / 1024;
    const maxMB = environment.files.default_file_size_limit;
    if (fileSizeMB > maxMB) {
      return {
        valid: false,
        errorMessage: this.translateService.instant('FORM_ERROR.FILE_SIZE_EXCEEDS_LIMIT', { fileName: file.name }),
      };
    }
    return { valid: true };
  }

  private setCurrentDocumentForUpload(document: DocumentModel, shipmentId: number): void {
    const documentsOfType = this.entryDocuments[shipmentId]?.filter((d) => d.type === document.type) ?? [];
    const indexOfCurrentDocumentOfType = documentsOfType.findIndex((d) => d.id === document.id);
    const documentNumber = indexOfCurrentDocumentOfType === -1 ? documentsOfType.length + 1 : indexOfCurrentDocumentOfType + 1;

    this.documentChangeCounter++;
    this.currentDocument = document;
    this.currentShipmentId = shipmentId;
    this.currentDocumentNumber = documentNumber;
    this.isForceAddDocument = false;
    this.isAdditionalPseudoDoc = false;
    this.isInvoiceReplace = false;
  }

  public onDragOver(event: DragEvent, doc: DocumentModel, shipmentId: number): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isDocumentUploaded(doc)) return;
    this.dragOverCardKey = this.getDropZoneKey(doc, shipmentId);
    this.cdr.markForCheck();
  }

  public onDrop(event: DragEvent, doc: DocumentModel, shipmentId: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverCardKey = null;
    this.cdr.markForCheck();
    if (this.isDocumentUploaded(doc)) return;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }
    if (files.length !== 1) {
      this.invalidFileErrorMessage = this.translateService.instant('FORM_ERROR.DROP_ONE_FILE_ONLY');
      this.isShowInvalidFileToast = true;
      this.cdr.markForCheck();
      return;
    }

    const file = files[0];
    const result = this.validateDroppedFile(file);
    if (!result.valid) {
      this.invalidFileErrorMessage = result.errorMessage ?? '';
      this.isShowInvalidFileToast = true;
      this.cdr.markForCheck();
      return;
    }

    if (this.isNonOcrDocType(doc)) {
      this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${doc.id}`, true);
      if (this.isPendingAdditionalDoc(doc, shipmentId)) {
        this.uploadAdditionalDocumentToServer(file, doc.type, shipmentId, doc);
      } else {
        this.uploadDocument(file, doc, shipmentId);
      }
      this.cdr.markForCheck();
      return;
    }

    this.setCurrentDocumentForUpload(doc, shipmentId);
    if (this.isPendingAdditionalDoc(doc, shipmentId)) {
      this.isForceAddDocument = true;
      this.isAdditionalPseudoDoc = true;
    }
    this.pendingDroppedFile = file;
    this.setIsOpenUploadModal(true);
    this.cdr.markForCheck();
  }

  public onDragLeave(): void {
    this.dragOverCardKey = null;
    this.cdr.markForCheck();
  }

  public dismissInvalidFileToast(): void {
    this.isShowInvalidFileToast = false;
    this.invalidFileErrorMessage = '';
    this.cdr.markForCheck();
  }

  public getPendingAdditionalDocs(shipmentId: number): DocumentModel[] {
    return this.pendingAdditionalDocuments.get(shipmentId) || [];
  }

  public trackByPendingDoc(index: number, doc: DocumentModel): number {
    return doc.id;
  }

  public pendingDocNumber(doc: DocumentModel, shipmentId: number): number {
    const serverDocsOfType = (this.entryDocuments[shipmentId] || []).filter((d) => d.type === doc.type).length;
    const pendingOfType = this.getPendingAdditionalDocs(shipmentId).filter((d) => d.type === doc.type);
    const pendingIndexOfType = pendingOfType.findIndex((d) => d.id === doc.id);
    return serverDocsOfType + pendingIndexOfType + 1;
  }

  public isPendingAdditionalDoc(doc: DocumentModel, shipmentId: number): boolean {
    return this.getPendingAdditionalDocs(shipmentId).some((d) => d.id === doc.id);
  }

  public isNonOcrDocType(doc: DocumentModel): boolean {
    return (
      doc.type === enumDocumentType.PACKING_LIST || doc.type === enumDocumentType.TRADE_CERTIFICATE || doc.type === enumDocumentType.UNKNOWN
    );
  }

  public onPendingAdditionalDocClick(doc: DocumentModel, shipmentId: number): void {
    if (this.isNonOcrDocType(doc)) {
      if (this.pendingNonOcrDoc !== null) {
        return;
      }
      this.pendingNonOcrDoc = doc;
      this.pendingNonOcrShipmentId = shipmentId;
      this.pendingNonOcrFileInput?.nativeElement?.click();
      this.cdr.markForCheck();
      return;
    }
    this.onDocumentUploadClick(doc, shipmentId, true, true);
  }

  public onPendingNonOcrFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    const doc = this.pendingNonOcrDoc;
    const shipmentId = this.pendingNonOcrShipmentId;
    this.pendingNonOcrDoc = null;
    this.pendingNonOcrShipmentId = null;
    if (input) {
      input.value = '';
      input.blur();
    }
    if (!file || !doc || shipmentId == null) {
      this.cdr.markForCheck();
      return;
    }
    const result = this.validateDroppedFile(file);
    if (!result.valid) {
      this.invalidFileErrorMessage = result.errorMessage ?? '';
      this.isShowInvalidFileToast = true;
      this.cdr.markForCheck();
      return;
    }
    this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${doc.id}`, true);
    if (this.isPendingAdditionalDoc(doc, shipmentId)) {
      this.uploadAdditionalDocumentToServer(file, doc.type, shipmentId, doc);
    } else {
      this.uploadDocument(file, doc, shipmentId);
    }
    this.cdr.markForCheck();
  }

  public removePendingAdditionalDoc(doc: DocumentModel, shipmentId: number): void {
    const list = this.pendingAdditionalDocuments.get(shipmentId) || [];
    this.pendingAdditionalDocuments.set(
      shipmentId,
      list.filter((d) => d.id !== doc.id),
    );
    this.overflowCheckPending = true;
    this.cdr.markForCheck();
  }

  public onOverflowChange(change) {
    const { isOverflowingHorizontally, isOverflowingVertically } = change;
    this.isDocStripOverflowing = isOverflowingHorizontally;
  }

  public onScrolledToStart() {
    this.isDocStripScrolledToEnd = false;
    this.isDocStripScrolledToStart = true;
  }

  public onScrolling() {
    this.isDocStripScrolledToEnd = false;
    this.isDocStripScrolledToStart = false;
  }

  public onScrolledToEnd() {
    this.isDocStripScrolledToStart = false;
    this.isDocStripScrolledToEnd = true;
  }

  public scrollToDirection(direction: 'left' | 'right') {
    const scrollAmount: number = this.defaultScrollDistance;
    let directionModifier: number;

    switch (direction) {
      case 'left':
        directionModifier = -1;
        break;
      case 'right':
        directionModifier = 1;
        break;
      default:
        break;
    }

    this.scrollByAmount(scrollAmount * directionModifier, 0);
  }

  public setIsOpenUploadModal(state: boolean) {
    this.isOpenUploadModal = state;
  }

  public setIsOpenDocsPreviewModal(state: boolean) {
    this.isOpenDocsPreviewModal = state;
  }

  public get documentUploadingStatusText(): string {
    const scanningStatuses = [enumOcrStatus.PROCESSING, enumOcrStatus.PARTIALLY_PROCESSED, enumOcrStatus.CLASSIFYING_LINE_ITEMS];
    return scanningStatuses.includes(this.ocrStatus) ? 'LABELS.SCANNING' : 'LABELS.UPLOADING';
  }

  public get documentUploadModalHeader(): string {
    let documentName: string = '';
    if (!this.currentDocument) {
      return this.translateService.instant('DOCUMENT_UPLOAD_MODAL.HEADER');
    }

    documentName = this.translateService.instant(
      this.documentUploadModalHeaderDocumentName(this.currentDocument, this.currentDocumentNumber),
    );

    return this.translateService.instant('DOCUMENT_UPLOAD_MODAL.HEADER', { documentName });
  }

  public get isShowPoaDocument(): boolean {
    return (
      this.entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED ||
      (this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
        this.entrySummary.bondRequest &&
        this.entrySummary.bondRequest.state === enumBondRequestState.CREATED)
    );
  }

  public get poaDocType(): enumDocumentType {
    return this.isShowPoaDocument ? enumDocumentType.POWER_OF_ATTORNEY : undefined;
  }

  public get isHideScrollLeft(): boolean {
    return !this.isDocStripOverflowing || this.isDocStripScrolledToStart;
  }

  public get isHideScrollRight(): boolean {
    return !this.isDocStripOverflowing || this.isDocStripScrolledToEnd;
  }

  public get entryPreviewModalDocs(): DocumentPreviewModel[] {
    return this.previewModalDocs?.filter((pmd, index, self) => {
      return index === self.findIndex((p) => p.id === pmd.id);
    });
  }

  private resetDocumentUploadFields() {
    this.currentDocument = null;
    this.currentShipmentId = null;
    this.currentDocumentNumber = null;
    this.isForceAddDocument = null;
    this.isAdditionalPseudoDoc = null;
    this.isInvoiceReplace = null;
    this.pendingDroppedFile = null;

    if (!this.isOpenUploadModal) {
      this.skipOcrScanControl.reset();
    }
  }

  private resetScrollingContainerParams() {
    this.isDocStripScrolledToStart = true;
    this.isDocStripScrolledToEnd = false;

    if (!this.scrollContainer || !this.scrollContainer.nativeElement) {
      return;
    }

    const container: HTMLElement = this.scrollContainer.nativeElement;
    container.scrollTo({
      left: 0,
    });
  }

  private uploadDocumentToServer(
    file: File,
    doc: DocumentModel,
    shipmentId: number,
    copyPoaToCompany: boolean = false,
    isForceAdd?: boolean,
    isAdditionalPseudoDoc?: boolean,
    isInvoiceReplace: boolean = false,
  ): void {
    this.ocrStatus = null;

    const typesToSkipOcrScan = [
      enumDocumentType.POWER_OF_ATTORNEY,
      enumDocumentType.POWER_OF_ATTORNEY_TRUCKER,
      enumDocumentType.PACKING_LIST,
      enumDocumentType.TRADE_CERTIFICATE,
      enumDocumentType.UNKNOWN,
    ];
    const isInvoiceUpload = doc.type === enumDocumentType.INVOICE;
    const isStartEsOcrScan = !this.skipOcrScanControl.value && !typesToSkipOcrScan.includes(doc.type);

    if (isStartEsOcrScan) {
      this.entrySummaryStateService.currentEsDocOCRState$.next(enumOcrStatus.PROCESSING);
    }

    let currentInvoice: InvoiceModel;

    if (isInvoiceUpload) {
      const currentShipment = this.entrySummary?.shipments?.find((sh) => sh.id === shipmentId);
      const invoices = currentShipment?.invoices;
      currentInvoice =
        isForceAdd && !isInvoiceReplace
          ? null
          : invoices?.length === 1
          ? invoices[0]
          : (invoices || []).find((i) => i.id === doc.relatedEntityId);
    }

    const replaceInvoice$: Observable<InvoiceModel | Partial<InvoiceModel>> = this.entrySummaryStateService
      .removeInvoice(shipmentId, currentInvoice?.id)
      .pipe(switchMap(() => this.entrySummaryStateService.createInvoice(shipmentId, {})));

    const createInvoice$: Observable<InvoiceModel | Partial<InvoiceModel>> = isInvoiceUpload
      ? !!currentInvoice
        ? of(currentInvoice)
        : isInvoiceReplace
        ? replaceInvoice$
        : this.entrySummaryStateService.createInvoice(shipmentId, {})
      : of(null);

    let createdDocId: number;
    let createdDocData: { file: File; docId: number; docHash: string };

    this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${doc.id}`, true);

    const isDocAlreadyExist = !!this.entryDocuments[shipmentId]?.find((document) => document.id === doc.id);

    createInvoice$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((invoice) => {
          const poaDocTypes = [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];
          const isPoaDoc = poaDocTypes.includes(doc.type);
          let documentToCreate: DocumentCreateModel = {
            fileName: file.name,
            rootEntityId: this.entrySummary?.id,
            rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
            relatedEntityId: !!invoice ? invoice?.id : shipmentId,
            relatedEntityType: !!invoice ? enumDocumentRelatedEntityType.INVOICE : enumDocumentRelatedEntityType.SHIPMENT,
            type: Number(doc.type),
          };

          if (isPoaDoc) {
            documentToCreate = {
              fileName: file.name,
              rootEntityId: this.entrySummary?.id,
              rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
              relatedEntityId: this.entrySummary?.id,
              relatedEntityType: enumDocumentRelatedEntityType.ENTRY_SUMMARY,
              type: Number(doc.type),
            };
          }

          let uploadDocument$: Observable<number>;

          if (isStartEsOcrScan) {
            uploadDocument$ = this.createDocument(documentToCreate, file, this.company?.id).pipe(
              switchMap((data) => {
                createdDocData = data;
                return of(data.docId);
              }),
            );
          } else {
            uploadDocument$ = this.entrySummaryStateService.uploadDocument(documentToCreate, file, false, this.company?.id);
          }

          return uploadDocument$;
        }),
        tap((docId) => (createdDocId = docId)),
        switchMap(() => {
          if (isStartEsOcrScan) {
            return this.entrySummaryStateService.processEsDocOCR(this.entrySummary?.id, createdDocData, false);
          } else {
            return of(enumOcrStatus.PROCESSED);
          }
        }),
        tap((_) => {
          if (createdDocId && copyPoaToCompany) {
            this.documentsService.copyPoaToCompany(createdDocId, file.name, false).pipe(take(1)).subscribe();
          }
        }),
        tap(() => {
          if (isForceAdd) {
            this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${createdDocId}`, true);
            this.cdr.detectChanges();
          }
        }),
        switchMap(() => {
          return this.getEntryDocuments(false);
        }),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService
          .updateDocuments(false)
          .pipe(
            switchMap(() => this.entrySummaryStateService.updateDocumentsState(false)),
            switchMap(() => this.entrySummaryStateService.loadEntrySummary()),
            switchMap((entry) => this.entrySummaryStateService.updateEntrySummary(entry)),
            tap((entry) => this.entrySummaryStateService.updateCurrentEntrySummary(entry)),
            take(1),
            finalize(() => {
              if (isStartEsOcrScan) {
                this.entrySummaryStateService.currentEsDocOCRState$.next(null);
              }
            }),
          )
          .subscribe(() => {
            this.getEntryDocuments(false)
              .pipe(take(1))
              .subscribe(() => {
                if (!isDocAlreadyExist && isAdditionalPseudoDoc) {
                  this.removePendingAdditionalDoc(doc, shipmentId);
                }
                this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${doc.id}`, false);

                if (isInvoiceUpload) {
                  this.entrySummaryStateService.invoiceSaved$.next(true);
                  this.entrySummaryStateService.invoiceCleared$.next(false);
                }

                if (!this.isOpenUploadModal) {
                  this.skipOcrScanControl.reset();
                }
                this.cdr.detectChanges();
              });
          });

        //start polling for document preview urls
        setTimeout(() => {
          this.pollEntryDocumentsPreviewUrl(createdDocId)
            .pipe(
              switchMap(() => this.entrySummaryStateService.updateDocuments(false)),
              finalize(() => {
                this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${createdDocId}`, false);
                this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${doc.id}`, false);
                this.isLoading = false;
                this.cdr.detectChanges();
              }),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }, DEFAULT_POLLING_TIMEOUT); //adding delay to give document time to upload before getting downlonad url
      });
  }

  private uploadAdditionalDocumentToServer(file: File, type: enumDocumentType, shipmentId: number, pendingDoc?: DocumentModel): void {
    const isInvoiceUpload = type === enumDocumentType.INVOICE;

    const createInvoice$: Observable<InvoiceModel | Partial<InvoiceModel>> = isInvoiceUpload
      ? this.entrySummaryStateService.createInvoice(shipmentId, {})
      : of(null);

    let createdDocId: number;
    if (!pendingDoc) {
      this.setAdditionalDocumentIsLoading(`es-additional-doc-input-${shipmentId}`, true);
    }
    createInvoice$
      .pipe(
        switchMap((invoice) => {
          return this.entrySummaryStateService.uploadDocument(
            {
              fileName: file.name,
              rootEntityId: this.entrySummary?.id,
              rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
              relatedEntityId: !!invoice ? invoice?.id : shipmentId,
              relatedEntityType: !!invoice ? enumDocumentRelatedEntityType.INVOICE : enumDocumentRelatedEntityType.SHIPMENT,
              type: Number(type),
            },
            file,
            false,
            this.company?.id,
          );
        }),
        tap((docId) => (createdDocId = docId)),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
        switchMap((entry) => of(this.entrySummaryStateService.updateCurrentEntrySummary(entry))),
        switchMap(() => this.getEntryDocuments(false)),
        takeUntil(this.destroy$),
        finalize(() => {
          if (isInvoiceUpload) {
            this.entrySummaryStateService.invoiceSaved$.next(true);
            this.entrySummaryStateService.invoiceCleared$.next(false);
          }
          if (pendingDoc) {
            this.removePendingAdditionalDoc(pendingDoc, shipmentId);
            this.setDocumentIsLoading(`${pendingDoc.type}-${shipmentId}-${pendingDoc.id}`, false);
          } else {
            this.setAdditionalDocumentIsLoading(`es-additional-doc-input-${shipmentId}`, false);
          }

          this.entrySummaryStateService
            .updateDocuments(true)
            .pipe(take(1))
            .subscribe(() => {
              this.cdr.detectChanges();
            });

          this.cdr.detectChanges();
          this.scrollByAmount(this.defaultScrollDistance);
        }),
      )
      .subscribe((docs) => {
        //start polling for document preview urls
        setTimeout(() => {
          this.pollEntryDocumentsPreviewUrl(createdDocId)
            .pipe(
              finalize(() => {
                if (pendingDoc) {
                  this.setDocumentIsLoading(`${pendingDoc.type}-${shipmentId}-${pendingDoc.id}`, false);
                } else {
                  this.setAdditionalDocumentIsLoading(`es-additional-doc-input-${shipmentId}`, false);
                }
                this.cdr.detectChanges();
              }),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }, DEFAULT_POLLING_TIMEOUT); //adding delay to give document time to upload before getting downlonad url
      });
  }

  private deleteDocumentFromServer(doc: DocumentModel, shipmentId: number): void {
    const isInvoiceUpload = doc.type === enumDocumentType.INVOICE;

    this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${doc.id}`, true);

    this.documentsService
      .deleteDocument(doc.id, false)
      .pipe(
        switchMap(() => this.getEntryDocuments(false)),
        switchMap(() => this.entrySummaryStateService.updateDocuments(true)),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber).pipe(take(1))),
        finalize(() => {
          if (isInvoiceUpload) {
            this.entrySummaryStateService.invoiceCleared$.next(true);
            this.entrySummaryStateService.invoiceSaved$.next(false);
          }
          this.setDocumentIsLoading(`${doc.type}-${shipmentId}-${doc.id}`, false);
          this.previewModalDocs = this.previewModalDocs.filter((pmd) => pmd.id !== doc.id);
          this.esOcrQuickFixService.setCurrentPreviewModalDocs(this.previewModalDocs);

          this.cdr.detectChanges();
          this.scrollByAmount(-this.defaultScrollDistance);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.cdr.detectChanges();
      });
  }

  private createDocument(
    document: DocumentCreateModel,
    file: File,
    companyId?: number,
    isShowLoader: boolean = false,
  ): Observable<{ file: File; docId: number; docHash: string }> {
    const fakeDocHash: string = new Date().toLocaleDateString();
    this.documentsService.isUploadingDocIdsToPush$.next(fakeDocHash);

    return this.documentsRepository
      .create(companyId || this.companiesService.getCompany().id, { ...document, fileName: file.name }, isShowLoader)
      .pipe(
        map((doc) => {
          return {
            file,
            docId: doc.id,
            docHash: fakeDocHash,
          };
        }),
      );
  }

  private getEntryDocument(docId: number, isShowLoader: boolean): Observable<DocumentModel> {
    return this.documentsService
      .getEntryDocuments(this.entrySummary?.id, isShowLoader)
      .pipe(map((documents: DocumentModel[]) => documents.find((doc) => doc.id === docId) || null));
  }

  private getEntryDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getEntryDocuments(this.entrySummary?.id, isShowLoader).pipe(
      tap((documents: DocumentModel[]) => {
        this.syncEntryDocuments(documents);
      }),
    );
  }

  private getEntryDocumentsPreviewUrl(createdDocId: number, isShowLoader: boolean): Observable<DocumentPreviewModel> {
    try {
      const entryDocumentsValues = [...Object.values(this.entryDocuments)];
      const entryDocuments = entryDocumentsValues.reduce((acc, curr) => acc.concat(curr), []);
      const doc = entryDocuments?.find((doc) => doc.id === createdDocId);

      if (!!doc?.previewDocumentId) {
        return this.documentsService.getDownloadLink(doc?.previewDocumentId, isShowLoader).pipe(
          map((response) => {
            let invoiceShipmentId: number;

            if (doc.type === enumDocumentType.INVOICE) {
              const invoiceShipment = this.shipments.find((shipment) => shipment.invoices.find((inv) => inv.id === doc.relatedEntityId));
              invoiceShipmentId = invoiceShipment?.id;
            }

            const shipmentId = doc.type === enumDocumentType.INVOICE ? invoiceShipmentId : doc.relatedEntityId;

            return {
              id: doc.id,
              url: response.downloadUrl,
              shipmentId,
              type: doc.type,
            };
          }),
          catchError((error) => {
            return of(null);
          }),
        );
      } else {
        return of(null);
      }
    } catch (err) {
      return of(null);
    }
  }

  private getEntryDocumentsPreviewUrls(isShowLoader: boolean): Observable<DocumentPreviewModel[]> {
    const entryDocumentsValues = [...Object.values(this.entryDocuments)];
    const entryDocuments = entryDocumentsValues.reduce((acc, curr) => acc.concat(curr), []);
    return of(
      entryDocuments.filter((doc) => {
        return doc.id > this.defaultBlDocuments.length;
      }),
    ).pipe(
      filter((docs: DocumentModel[]) => !!docs?.length),
      mergeMap((docs) => from(docs)),
      mergeMap((doc) => {
        if (!!doc?.previewDocumentId) {
          return this.documentsService.getDownloadLink(doc?.previewDocumentId, isShowLoader).pipe(
            map((response) => {
              let invoiceShipmentId: number;

              if (doc.type === enumDocumentType.INVOICE) {
                const invoiceShipment = this.shipments.find((shipment) => shipment.invoices.find((inv) => inv.id === doc.relatedEntityId));
                invoiceShipmentId = invoiceShipment?.id;
              }

              const shipmentId = doc.type === enumDocumentType.INVOICE ? invoiceShipmentId : doc.relatedEntityId;

              return {
                id: doc.id,
                url: response.downloadUrl,
                shipmentId,
                type: doc.type,
              };
            }),
            catchError((error) => {
              //return pseudo object to prevent polling interruptions
              return of({
                id: -999,
                url: '',
                shipmentId: -1000,
                type: enumDocumentType.INVOICE,
              });
            }),
          );
        } else {
          return of({
            id: -999,
            url: '',
            shipmentId: -1000,
            type: enumDocumentType.INVOICE,
          });
        }
      }),
      toArray(),
    );
  }

  private pollEntryDocumentsPreviewUrl(createdDocId: number): Observable<DocumentPreviewModel> {
    let pollAttempts = 0;
    this.previewModalDocs = this.entryPreviewModalDocs;

    return interval(DEFAULT_LONGER_POLLING_TIMEOUT).pipe(
      switchMap(() => this.getEntryDocument(createdDocId, false)),
      switchMap(() => this.getEntryDocumentsPreviewUrl(createdDocId, false)),
      tap((uploadedDocPreview) => {
        if (!!uploadedDocPreview && !!uploadedDocPreview?.url) {
          const sanitizedPreviewBgUrl = this.sanitizer.sanitize(SecurityContext.STYLE, `url("${uploadedDocPreview.url}")`);
          this.setDocsPreviewUrls(uploadedDocPreview.id, sanitizedPreviewBgUrl);
          this.previewModalDocs.push({
            id: uploadedDocPreview.id,
            url: sanitizedPreviewBgUrl,
            shipmentId: uploadedDocPreview.shipmentId,
            type: uploadedDocPreview.type,
          });
          this.esOcrQuickFixService.setCurrentPreviewModalDocs(this.previewModalDocs);
        }

        pollAttempts++;
        this.cdr.detectChanges();
      }),

      takeWhile((uploadedDocPreview) => {
        return pollAttempts < DEFAULT_MAX_POLLING_RETRY && !uploadedDocPreview?.url;
      }, true),
      switchMap((uploadedDocPreview) => {
        return this.getEntryDocuments(false).pipe(
          take(1),
          map((_) => uploadedDocPreview),
        );
      }),
    );
  }

  private setEntryDocumentsPreviewUrls(docsPreviewUrls: DocumentPreviewModel[]) {
    const previewModalDocs = [];
    docsPreviewUrls.forEach((dpu) => {
      if (!!dpu.url) {
        const sanitizedPreviewBgUrl = this.sanitizer.sanitize(SecurityContext.STYLE, `url('${dpu.url}')`);
        this.setDocsPreviewUrls(dpu.id, sanitizedPreviewBgUrl);
        previewModalDocs.push({
          id: dpu.id,
          url: sanitizedPreviewBgUrl,
          shipmentId: dpu.shipmentId,
          type: dpu.type,
        });
        this.cdr.detectChanges();
      }
    });

    const sortedPreviewModalDocs = this.getSortedPreviewModalDocs(previewModalDocs);
    this.previewModalDocs = sortedPreviewModalDocs;
    this.esOcrQuickFixService.setCurrentPreviewModalDocs(this.previewModalDocs);
  }

  private syncEntryDocuments(documents: DocumentModel[]) {
    this.rawDocuments = documents;
    this.entryDocuments = this.filteredDocuments(documents);
    this.overflowCheckPending = true;
  }

  private syncEntryDocument(shipmentId: number, docId: number, isShowLoader: boolean) {
    if (!this.entryDocuments) {
      return of(null);
    }

    return this.getEntryDocument(docId, isShowLoader).pipe(
      tap((doc) => {
        const currentShipmentDocuments = this.entryDocuments[shipmentId].filter((doc) => {
          const isEntryLevelDoc = this.entryLevelDocuments.some((eld) => eld.id === doc.id);
          return doc.type === doc.type && !isEntryLevelDoc;
        });

        const currentDocIndex = this.entryDocuments[shipmentId].findIndex((ed) => ed.id === doc.id);

        if (currentDocIndex === -1) {
          const farthestTypeIndex = UtilsHelper.findLastIndex(currentShipmentDocuments, 'type', doc.type);
          if (farthestTypeIndex === -1) {
            return;
          }

          currentShipmentDocuments.splice(farthestTypeIndex, 0, doc);
        } else {
          currentShipmentDocuments[currentDocIndex] = doc;
        }
      }),
    );
  }

  private filteredDocuments(documents: DocumentModel[]): AttachedDocumentsModel {
    const shipments = this.entrySummary.shipments;
    const typesToIgnoreFiltering: enumDocumentType[] = [enumDocumentType.POWER_OF_ATTORNEY_TRUCKER, enumDocumentType.POWER_OF_ATTORNEY];
    const typesToSkip: enumDocumentType[] = [enumDocumentType.FILE_PREVIEW, enumDocumentType.INVOICE];

    //#12108 - set entry level docs seperately as related to first shipment
    this.entryLevelDocuments = [...documents]
      .filter((doc) => isEntryLevelEsDocument(doc, this.entrySummary.id, typesToSkip))
      .map((doc) => remapEntryLevelDocToFirstShipment(doc, shipments[0]?.id));

    const skippedExcludedDocuments: DocumentModel[] = documents
      .filter((doc: DocumentModel) => !typesToSkip.includes(doc.type))
      .map((doc) => {
        //#12108 - map entry level docs to match first shipment
        if (isEntryLevelEsDocument(doc, this.entrySummary.id, typesToSkip)) {
          remapEntryLevelDocToFirstShipment(doc, shipments[0]?.id);
        }
        return doc;
      });

    const invoiceDocuments: DocumentModel[] = documents.filter((doc: DocumentModel) => doc.type === enumDocumentType.INVOICE);

    let attachedDocuments: AttachedDocumentsModel = {};

    shipments.forEach((shipment, index) => {
      const invoices = shipment.invoices;
      const currentShipmentInvoiceDocs: DocumentModel[] = [];

      //Add either existing doc or placeholder per shipment invoice
      invoices.forEach((invoice, index) => {
        const currentInvoiceDoc = invoiceDocuments.find((inD) => inD.relatedEntityId === invoice.id);
        if (!!currentInvoiceDoc) {
          currentInvoiceDoc.order = index;
          currentShipmentInvoiceDocs.push(currentInvoiceDoc);
        } else {
          currentShipmentInvoiceDocs.push({
            id: (index + 1) * -1,
            order: index,
            rootEntityId: this.entrySummary.id,
            rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
            relatedEntityId: invoice.id,
            relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
            url: null,
            createdDate: null,
            fileName: null,
            type: enumDocumentType.INVOICE,
            state: null,
          });
        }
      });

      attachedDocuments[shipment.id] = [];

      let defaultBlDocuments = [...this.defaultBlDocuments];

      if (invoices?.length) {
        defaultBlDocuments = this.defaultBlDocuments.filter((type) => type !== enumDocumentType.INVOICE);
      } else {
        defaultBlDocuments.push(enumDocumentType.INVOICE);
      }

      //set the default document upload inputs
      defaultBlDocuments.forEach((documentType: enumDocumentType, index: number) => {
        if (
          !skippedExcludedDocuments.find((document: DocumentModel) => {
            const poaDocTypes = [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];

            let comparisonId: number = shipment.id;

            if (poaDocTypes.includes(documentType)) {
              comparisonId = this.entrySummary.id;
            }

            return document.type === documentType && document.relatedEntityId === comparisonId;
          })
        ) {
          attachedDocuments[shipment.id].push({
            id: index,
            rootEntityId: this.entrySummary.id,
            rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
            relatedEntityId: shipment.id,
            relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
            url: null,
            createdDate: null,
            fileName: null,
            type: documentType,
            state: null,
          });
        }
      });

      //add invoices related to shipment
      attachedDocuments[shipment.id].push(...currentShipmentInvoiceDocs);

      //add uploaded documents if related to shipment(prevent duplicates)
      attachedDocuments[shipment.id].push(
        ...skippedExcludedDocuments.filter((doc: DocumentModel) => {
          const poaDocTypes = [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER];
          let comparisonId: number = shipment.id;
          if (poaDocTypes.includes(doc.type)) {
            comparisonId = this.entrySummary.id;
          }
          return doc.relatedEntityId === comparisonId;
        }),
      );

      attachedDocuments[shipment.id] = [
        ...attachedDocuments[shipment.id]
          .filter((doc: DocumentModel) => doc.type === enumDocumentType.BILL_OF_LADING)
          .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
        ...attachedDocuments[shipment.id]
          .filter((doc: DocumentModel) => doc.type === enumDocumentType.INVOICE)
          .sort((a, b) => this.sortOrder(a?.order, b?.order)),
        ...attachedDocuments[shipment.id]
          .filter((doc: DocumentModel) => doc.type === enumDocumentType.ARRIVAL_NOTICE)
          .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
        ...attachedDocuments[shipment.id]
          .filter((doc: DocumentModel) => doc.type === enumDocumentType.PACKING_LIST)
          .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
        ...attachedDocuments[shipment.id]
          .filter((doc: DocumentModel) => !defaultBlDocuments.includes(doc.type) && !typesToSkip.includes(doc.type))
          .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
        ...skippedExcludedDocuments
          .filter((doc: DocumentModel) => index === 0 && typesToIgnoreFiltering.includes(doc.type))
          .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
      ];
    });

    return attachedDocuments;
  }

  private sortOrder(a: number, b: number): number {
    return a - b;
  }

  private sortDates(a: string, b: string): number {
    return new Date(a).getTime() - new Date(b).getTime();
  }

  private setDocsPreviewUrls(id: number, value: string): void {
    this.docsPreviewUrls.set(id, value);
  }

  private setDocumentIsLoading(id: string, value: boolean): void {
    this.isLoadingDocUploadFlags.set(id, value);
  }

  private setAdditionalDocumentIsLoading(id: string, value: boolean): void {
    this.isLoadingAdditionalDocUploadFlags.set(id, value);
  }

  private scrollByAmount(scrollOffset: number, timeout: number = 500) {
    setTimeout(() => {
      const container: HTMLElement = this.scrollContainer.nativeElement;
      container.scrollTo({
        left: container.scrollLeft + scrollOffset,
        behavior: 'smooth',
      });
    }, timeout);
  }

  private getSortedPreviewModalDocs(previewModalDocs: DocumentPreviewModel[]) {
    const previewModalDocsListByShipmentId = UtilsHelper.groupObjectsByField(previewModalDocs, 'shipmentId');

    const sortedPreviewModalDocs: DocumentPreviewModel[] = [];

    const typeSortingOrder = [
      enumDocumentType.POWER_OF_ATTORNEY,
      enumDocumentType.POWER_OF_ATTORNEY_TRUCKER,
      enumDocumentType.BILL_OF_LADING,
      enumDocumentType.INVOICE,
      enumDocumentType.ARRIVAL_NOTICE,
      enumDocumentType.PACKING_LIST,
      enumDocumentType.TRADE_CERTIFICATE,
      enumDocumentType.UNKNOWN,
    ];

    Object.keys(previewModalDocsListByShipmentId).forEach((key) => {
      const currentShipmentPreviewModalDocs = previewModalDocsListByShipmentId[key];
      currentShipmentPreviewModalDocs.sort((a, b) => {
        return typeSortingOrder.indexOf(a.type) - typeSortingOrder.indexOf(b.type);
      });

      sortedPreviewModalDocs.push(...currentShipmentPreviewModalDocs);
    });

    return sortedPreviewModalDocs;
  }

  private get hasPoaDocusignFlag(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.poaDocusign);
  }

  private get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  private get isBillOfLadingRequired(): boolean {
    return this.entrySummary?.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED && !this.isTruck;
  }
}
